import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { TreeNode } from "@/components/ui/TreeNode";
import { useTheme } from "@/hooks/useTheme";
import type { QuestionType, OptionType } from "@/types/db/courses";

export interface QuestionCardProps {
  question: QuestionType;
  questionIndex: number;
  isLast: boolean;
  onUpdate: (updates: Partial<QuestionType>) => void;
  onDelete: () => void;
  onChangeType: (newType: QuestionType["type"]) => void;
}

export function QuestionCard({ question, questionIndex, isLast, onUpdate, onDelete, onChangeType }: QuestionCardProps) {
  const { colors } = useTheme();
  const [showTypeDropdown, setShowTypeDropdown] = React.useState(false);

  const questionTypeLabels: Record<QuestionType["type"], string> = {
    single: "Single Choice",
    multiple: "Multiple Choice",
    true_false: "True/False",
    short: "Short Answer",
    ordering: "Ordering",
    matching: "Matching",
  };

  const questionTypes: QuestionType["type"][] = ["single", "multiple", "true_false", "short", "ordering", "matching"];

  // Helper to add an option to single/multiple choice questions
  const addOption = () => {
    if (question.type === "single" || question.type === "multiple") {
      const newOption: OptionType = {
        id: `opt-${Date.now()}`,
        text: "",
      };
      onUpdate({
        ...question,
        options: [...question.options, newOption],
      });
    }
  };

  // Helper to update an option
  const updateOption = (optionIndex: number, updates: Partial<OptionType>) => {
    if (question.type === "single" || question.type === "multiple") {
      const newOptions = [...question.options];
      newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
      onUpdate({
        ...question,
        options: newOptions,
      });
    }
  };

  // Helper to remove an option
  const removeOption = (optionIndex: number) => {
    if (question.type === "single" || question.type === "multiple") {
      const newOptions = question.options.filter((_, i) => i !== optionIndex);
      onUpdate({
        ...question,
        options: newOptions,
      });
    }
  };

  // Helper to toggle correct answer for single choice
  const toggleSingleCorrect = (optionId: string) => {
    if (question.type === "single") {
      onUpdate({
        ...question,
        correct: optionId,
      });
    }
  };

  // Helper to toggle correct answer for multiple choice
  const toggleMultipleCorrect = (optionId: string) => {
    if (question.type === "multiple") {
      const isSelected = question.correct.includes(optionId);
      const newCorrect = isSelected
        ? question.correct.filter((id) => id !== optionId)
        : [...question.correct, optionId];
      onUpdate({
        ...question,
        correct: newCorrect,
      });
    }
  };

  // Helper to add item for ordering questions
  const addOrderingItem = () => {
    if (question.type === "ordering") {
      const newItem = {
        id: `item-${Date.now()}`,
        text: "",
      };
      onUpdate({
        ...question,
        items: [...question.items, newItem],
        correct: [...question.correct, newItem.id],
      });
    }
  };

  // Helper to update ordering item
  const updateOrderingItem = (itemIndex: number, text: string) => {
    if (question.type === "ordering") {
      const newItems = [...question.items];
      newItems[itemIndex] = { ...newItems[itemIndex], text };
      onUpdate({
        ...question,
        items: newItems,
      });
    }
  };

  // Helper to remove ordering item
  const removeOrderingItem = (itemIndex: number) => {
    if (question.type === "ordering") {
      const itemId = question.items[itemIndex].id;
      const newItems = question.items.filter((_, i) => i !== itemIndex);
      const newCorrect = question.correct.filter((id) => id !== itemId);
      onUpdate({
        ...question,
        items: newItems,
        correct: newCorrect,
      });
    }
  };

  // Helper to add matching pair
  const addMatchingPair = (side: "left" | "right") => {
    if (question.type === "matching") {
      const newItem = {
        id: `${side}-${Date.now()}`,
        text: "",
      };
      onUpdate({
        ...question,
        [side]: [...question[side], newItem],
      });
    }
  };

  // Helper to update matching item
  const updateMatchingItem = (side: "left" | "right", itemIndex: number, text: string) => {
    if (question.type === "matching") {
      const newItems = [...question[side]];
      newItems[itemIndex] = { ...newItems[itemIndex], text };
      onUpdate({
        ...question,
        [side]: newItems,
      });
    }
  };

  // Helper to remove matching item
  const removeMatchingItem = (side: "left" | "right", itemIndex: number) => {
    if (question.type === "matching") {
      const itemId = question[side][itemIndex].id;
      const newItems = question[side].filter((_, i) => i !== itemIndex);
      // Remove any pairs that reference this item
      const newCorrect = question.correct.filter(([leftId, rightId]) =>
        side === "left" ? leftId !== itemId : rightId !== itemId,
      );
      onUpdate({
        ...question,
        [side]: newItems,
        correct: newCorrect,
      });
    }
  };

  // Helper to add acceptable answer for short answer
  const addAcceptableAnswer = () => {
    if (question.type === "short") {
      onUpdate({
        ...question,
        acceptable: [...question.acceptable, ""],
      });
    }
  };

  // Helper to update acceptable answer
  const updateAcceptableAnswer = (index: number, text: string) => {
    if (question.type === "short") {
      const newAcceptable = [...question.acceptable];
      newAcceptable[index] = text;
      onUpdate({
        ...question,
        acceptable: newAcceptable,
      });
    }
  };

  // Helper to remove acceptable answer
  const removeAcceptableAnswer = (index: number) => {
    if (question.type === "short") {
      const newAcceptable = question.acceptable.filter((_, i) => i !== index);
      onUpdate({
        ...question,
        acceptable: newAcceptable,
      });
    }
  };

  return (
    <TreeNode depth={1} isLast={isLast}>
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <ThemedText type="defaultSemiBold">Question {questionIndex + 1}</ThemedText>
            <Pressable
              onPress={() => setShowTypeDropdown(!showTypeDropdown)}
              style={[styles.badge, { backgroundColor: colors.primary + "20" }]}
            >
              <ThemedText style={[styles.badgeText, { color: colors.primary }]}>
                {questionTypeLabels[question.type]}
              </ThemedText>
              <ThemedText style={[styles.badgeText, { color: colors.primary, marginLeft: 4 }]}>
                {showTypeDropdown ? "▲" : "▼"}
              </ThemedText>
            </Pressable>
          </View>
          <Button
            icon="trash-can-outline"
            onPress={onDelete}
            iconColor="#ef4444"
            testID={`delete-question-${questionIndex}`}
            style={{ backgroundColor: "transparent" }}
          />
        </View>

        {/* Question Type Dropdown */}
        {showTypeDropdown && (
          <View style={[styles.typeDropdown, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {questionTypes.map((type) => (
              <Pressable
                key={type}
                onPress={() => {
                  if (type !== question.type) {
                    onChangeType(type);
                  }
                  setShowTypeDropdown(false);
                }}
                style={[
                  styles.typeOption,
                  {
                    backgroundColor: type === question.type ? colors.primary + "10" : "transparent",
                  },
                ]}
              >
                <ThemedText
                  style={{
                    color: type === question.type ? colors.primary : colors.text,
                    fontWeight: type === question.type ? "600" : "400",
                  }}
                >
                  {questionTypeLabels[type]}
                </ThemedText>
              </Pressable>
            ))}
          </View>
        )}

        {/* Prompt */}
        <ThemedInput
          label="Question Prompt"
          value={question.prompt}
          onChangeText={(prompt) => onUpdate({ ...question, prompt })}
          placeholder="Enter your question..."
          multiline
          numberOfLines={3}
          inputStyle={{ minHeight: 80, textAlignVertical: "top" }}
          testID={`question-${questionIndex}-prompt`}
        />

        {/* Points */}
        <ThemedInput
          label="Points"
          value={String(question.points)}
          onChangeText={(text) => onUpdate({ ...question, points: parseInt(text) || 1 })}
          keyboardType="numeric"
          placeholder="1"
          testID={`question-${questionIndex}-points`}
        />

        {/* Skippable */}
        <View style={styles.toggleRow}>
          <View style={{ flex: 1 }}>
            <ThemedText style={styles.label}>Skippable</ThemedText>
            <ThemedText style={styles.helperText}>Allow students to skip this question</ThemedText>
          </View>
          <ToggleSwitch
            value={question.skippable}
            onValueChange={(val) => onUpdate({ ...question, skippable: val })}
            testID={`question-${questionIndex}-skippable`}
          />
        </View>

        {/* Question Type Specific Fields */}
        {(question.type === "single" || question.type === "multiple") && (
          <View style={styles.section}>
            <ThemedText style={styles.label}>Answer Options</ThemedText>
            {question.options.map((option, optionIndex) => (
              <View key={option.id} style={styles.optionRow}>
                <View style={styles.optionCheckbox}>
                  <Pressable
                    onPress={() =>
                      question.type === "single" ? toggleSingleCorrect(option.id) : toggleMultipleCorrect(option.id)
                    }
                    style={[
                      styles.checkbox,
                      {
                        borderColor: colors.border,
                        backgroundColor:
                          (question.type === "single" && question.correct === option.id) ||
                          (question.type === "multiple" && question.correct.includes(option.id))
                            ? colors.primary
                            : "transparent",
                      },
                    ]}
                    testID={`question-${questionIndex}-option-${optionIndex}-correct`}
                  />
                </View>
                <ThemedInput
                  value={option.text}
                  onChangeText={(text) => updateOption(optionIndex, { text })}
                  placeholder={`Option ${optionIndex + 1}`}
                  style={{ flex: 1 }}
                  testID={`question-${questionIndex}-option-${optionIndex}`}
                />
                <Button
                  icon="close-circle-outline"
                  onPress={() => removeOption(optionIndex)}
                  iconColor="#ef4444"
                  testID={`question-${questionIndex}-option-${optionIndex}-delete`}
                  style={{ backgroundColor: "transparent" }}
                />
              </View>
            ))}
            <Button
              icon="plus"
              text="Add Option"
              onPress={addOption}
              testID={`question-${questionIndex}-add-option`}
              style={{ borderWidth: 1, borderColor: colors.border, paddingHorizontal: 12, paddingVertical: 8 }}
            />
          </View>
        )}

        {question.type === "true_false" && (
          <View style={styles.section}>
            <ThemedText style={styles.label}>Correct Answer</ThemedText>
            <View style={styles.toggleGroup}>
              <Pressable
                onPress={() => onUpdate({ ...question, correct: true })}
                style={[
                  styles.toggleButton,
                  {
                    backgroundColor: question.correct === true ? colors.primary : colors.backgroundSecondary,
                    borderColor: question.correct === true ? colors.primary : colors.border,
                  },
                ]}
                testID={`question-${questionIndex}-true`}
              >
                <ThemedText style={{ color: question.correct === true ? "white" : colors.text }}>True</ThemedText>
              </Pressable>
              <Pressable
                onPress={() => onUpdate({ ...question, correct: false })}
                style={[
                  styles.toggleButton,
                  {
                    backgroundColor: question.correct === false ? colors.primary : colors.backgroundSecondary,
                    borderColor: question.correct === false ? colors.primary : colors.border,
                  },
                ]}
                testID={`question-${questionIndex}-false`}
              >
                <ThemedText style={{ color: question.correct === false ? "white" : colors.text }}>False</ThemedText>
              </Pressable>
            </View>
          </View>
        )}

        {question.type === "short" && (
          <View style={styles.section}>
            <ThemedText style={styles.label}>Acceptable Answers</ThemedText>
            {question.acceptable.map((answer, answerIndex) => (
              <View key={answerIndex} style={styles.optionRow}>
                <ThemedInput
                  value={answer}
                  onChangeText={(text) => updateAcceptableAnswer(answerIndex, text)}
                  placeholder={`Answer ${answerIndex + 1}`}
                  style={{ flex: 1 }}
                  testID={`question-${questionIndex}-answer-${answerIndex}`}
                />
                <Button
                  icon="close-circle-outline"
                  onPress={() => removeAcceptableAnswer(answerIndex)}
                  iconColor="#ef4444"
                  testID={`question-${questionIndex}-answer-${answerIndex}-delete`}
                  style={{ backgroundColor: "transparent" }}
                />
              </View>
            ))}
            <Button
              icon="plus"
              text="Add Acceptable Answer"
              onPress={addAcceptableAnswer}
              testID={`question-${questionIndex}-add-answer`}
              style={{ borderWidth: 1, borderColor: colors.border, paddingHorizontal: 12, paddingVertical: 8 }}
            />
            <View style={styles.toggleRow}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.label}>Case Sensitive</ThemedText>
              </View>
              <ToggleSwitch
                value={question.caseSensitive}
                onValueChange={(val) => onUpdate({ ...question, caseSensitive: val })}
                testID={`question-${questionIndex}-case-sensitive`}
              />
            </View>
            <View style={styles.toggleRow}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.label}>Normalize Whitespace</ThemedText>
              </View>
              <ToggleSwitch
                value={question.normalizeWhitespace}
                onValueChange={(val) => onUpdate({ ...question, normalizeWhitespace: val })}
                testID={`question-${questionIndex}-normalize-whitespace`}
              />
            </View>
          </View>
        )}

        {question.type === "ordering" && (
          <View style={styles.section}>
            <ThemedText style={styles.label}>Items (in correct order)</ThemedText>
            {question.items.map((item, itemIndex) => (
              <View key={item.id} style={styles.optionRow}>
                <ThemedText style={styles.orderNumber}>{itemIndex + 1}.</ThemedText>
                <ThemedInput
                  value={item.text}
                  onChangeText={(text) => updateOrderingItem(itemIndex, text)}
                  placeholder={`Item ${itemIndex + 1}`}
                  style={{ flex: 1 }}
                  testID={`question-${questionIndex}-item-${itemIndex}`}
                />
                <Button
                  icon="close-circle-outline"
                  onPress={() => removeOrderingItem(itemIndex)}
                  iconColor="#ef4444"
                  testID={`question-${questionIndex}-item-${itemIndex}-delete`}
                  style={{ backgroundColor: "transparent" }}
                />
              </View>
            ))}
            <Button
              icon="plus"
              text="Add Item"
              onPress={addOrderingItem}
              testID={`question-${questionIndex}-add-item`}
              style={{ borderWidth: 1, borderColor: colors.border, paddingHorizontal: 12, paddingVertical: 8 }}
            />
          </View>
        )}

        {question.type === "matching" && (
          <View style={styles.section}>
            <View style={styles.matchingColumns}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.label}>Left Column</ThemedText>
                {question.left.map((item, itemIndex) => (
                  <View key={item.id} style={styles.optionRow}>
                    <ThemedInput
                      value={item.text}
                      onChangeText={(text) => updateMatchingItem("left", itemIndex, text)}
                      placeholder={`Left ${itemIndex + 1}`}
                      style={{ flex: 1 }}
                      testID={`question-${questionIndex}-left-${itemIndex}`}
                    />
                    <Button
                      icon="close-circle-outline"
                      onPress={() => removeMatchingItem("left", itemIndex)}
                      iconColor="#ef4444"
                      testID={`question-${questionIndex}-left-${itemIndex}-delete`}
                      style={{ backgroundColor: "transparent" }}
                    />
                  </View>
                ))}
                <Button
                  icon="plus"
                  text="Add Left"
                  onPress={() => addMatchingPair("left")}
                  testID={`question-${questionIndex}-add-left`}
                  style={{ borderWidth: 1, borderColor: colors.border, paddingHorizontal: 12, paddingVertical: 8 }}
                />
              </View>

              <View style={{ flex: 1 }}>
                <ThemedText style={styles.label}>Right Column</ThemedText>
                {question.right.map((item, itemIndex) => (
                  <View key={item.id} style={styles.optionRow}>
                    <ThemedInput
                      value={item.text}
                      onChangeText={(text) => updateMatchingItem("right", itemIndex, text)}
                      placeholder={`Right ${itemIndex + 1}`}
                      style={{ flex: 1 }}
                      testID={`question-${questionIndex}-right-${itemIndex}`}
                    />
                    <Button
                      icon="close-circle-outline"
                      onPress={() => removeMatchingItem("right", itemIndex)}
                      iconColor="#ef4444"
                      testID={`question-${questionIndex}-right-${itemIndex}-delete`}
                      style={{ backgroundColor: "transparent" }}
                    />
                  </View>
                ))}
                <Button
                  icon="plus"
                  text="Add Right"
                  onPress={() => addMatchingPair("right")}
                  testID={`question-${questionIndex}-add-right`}
                  style={{ borderWidth: 1, borderColor: colors.border, paddingHorizontal: 12, paddingVertical: 8 }}
                />
              </View>
            </View>
            <ThemedText style={[styles.helperText, { marginTop: 8 }]}>
              Note: Correct pairs need to be configured separately after creation
            </ThemedText>
          </View>
        )}
      </View>
    </TreeNode>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 16,
    marginBottom: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  typeDropdown: {
    borderWidth: 1,
    borderRadius: 8,
    marginTop: 8,
    overflow: "hidden",
  },
  typeOption: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.05)",
  },
  section: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.7,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 8,
  },
  toggleGroup: {
    flexDirection: "row",
    gap: 8,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  optionCheckbox: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
  },
  orderNumber: {
    fontSize: 14,
    fontWeight: "600",
    minWidth: 24,
  },
  matchingColumns: {
    flexDirection: "row",
    gap: 16,
  },
});
